import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment-timezone";
import { Subscription } from "rxjs/Subscription";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { VISIT_PAGE } from "../../../app/access-control/common-permission-asks";
import { Pages } from "../../../app/access-control/permissions";
import { OPEN_VISIT_STATUS_SETS } from "../../../configs/clinical-codes.constants";
import { NavigationService } from "../../../navigation/navigation.service";
import { AssessmentDataService } from "../../../providers/asmt-data-service/asmt-data-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

/**
 * CO-3455 passive open-visit indicator (BDD-CO-3382 Section 4).
 *
 * Read-only, non-blocking: lists every OTHER open visit for the patient
 * (excludeAssessmentId omits the visit currently being documented/started).
 * Gated on the concurrent_visits flag (concurrentVisitsToggle); absent flag
 * or empty list renders nothing (4.11 - absent, not present-but-empty).
 * Fetch-on-init IS the refresh-on-navigation semantics (4.10, DR-4) for a
 * freshly created host: no push, no polling. A host cached by Ionic's nav
 * stack (patChartId/excludeAssessmentId unchanged, so ngOnChanges never
 * fires again) must call refresh() itself on re-entry - see CO-3455 pop-back
 * fix, CO-3382 wave-1 finding 6.
 *
 * CO-3448 drill-down (BDD 4.12/4.13): a line becomes activatable only when
 * the viewer's Permission Group grants Pages.Visit read access; canDrillDown
 * resolves once per mount and gates both the template and activate() itself,
 * fail-closed on any error so a permission-check failure never opens a link.
 * Navigation always uses externalPatChartId, never patChartId: patChartId is
 * whatever id the host page happens to hold (internal on the Create Visit
 * modal's calendar path, and briefly internal on Section after its patient
 * lookup resolves - see section.ts), but resolvePatientSessionFromExternalId
 * and the AssessmentHomePage nav param both require the EXTERNAL id.
 *
 * Known platform behavior, not a bug: NavigationService.goToInternalPage
 * silently drops a call to the same page name fired within 1s of the last one
 * (NOVA-1652 in-flight navigation guard), so two drill-downs to Assessment
 * Home inside that window collapse to the first.
 *
 * OFFLINE: intentionally absent in this wave. The fetch is a plain HTTP GET
 * and its error path fails soft to no lines. The duplicate-start dialog DOES
 * work offline (it reads the in-memory visitsData snapshot via nav params);
 * sourcing this component from the offline SQLite store is deferred to the
 * mobile-parity follow-up (CO-3446).
 */
@Component({
  selector: "open-visit-indicator",
  templateUrl: "open-visit-indicator.html",
})
export class OpenVisitIndicatorComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() patChartId: string;
  @Input() excludeAssessmentId: string;
  // CO-3448: the EXTERNAL (QBOS) chart id, used ONLY for drill-down
  // navigation (resolvePatientSessionFromExternalId + the AssessmentHomePage
  // nav param). Deliberately separate from patChartId, which stays whatever
  // id the visit-data fetch has always used - the two are not interchangeable
  // on every host (see the class doc). Drill-down does not render without it.
  @Input() externalPatChartId: string;

  // Emitted synchronously, before the drill-down navigation is kicked off, so
  // a host that has its own dismissable UI in front of the indicator (the
  // Create Visit modal) can close itself first. No-op for hosts that don't
  // bind it (Assessment Home, Section).
  @Output() beforeNavigate = new EventEmitter<void>();

  lines: string[] = [];
  canDrillDown = false;

  // Single gate the template and activate() both read, so "permitted" and
  // "has an external id to navigate with" can never be checked inconsistently.
  get canActivate(): boolean {
    return this.canDrillDown && !!this.externalPatChartId;
  }

  private openVisits: any[] = [];
  private toggleSub: Subscription;
  private permissionSub: Subscription;

  constructor(
    private dataService: AssessmentDataService,
    private stateData: StateManagementService,
    private translate: TranslateService,
    private accessControlService: AccessControlService,
    private navSvc: NavigationService
  ) {}

  ngOnInit(): void {
    this.toggleSub = this.stateData.concurrentVisitsToggle.subscribe(
      (isOn: boolean) => {
        if (isOn) {
          this.loadOpenVisits();
        } else {
          // Null (never fetched) and false both resolve to "off" per contract.
          // Clear openVisits too, or a stale entry survives to be activated
          // (CO-3448: activate() indexes into openVisits, not lines).
          this.lines = [];
          this.openVisits = [];
        }
      }
    );
    this.permissionSub = this.accessControlService
      .getPermissionWhenAuthenticated(VISIT_PAGE)
      .subscribe(
        (data) => {
          this.canDrillDown = this.accessControlService.hasAccess(
            data,
            Pages.Visit
          );
        },
        () => {
          // Fail closed (4.13): a permission-check error must never open a link.
          this.canDrillDown = false;
        }
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Assessment Home and Section assign or reassign these inputs in
    // ionViewDidLoad, which fires after this component's ngOnInit, so a late
    // input change must trigger the load itself. The first change (inputs
    // bound at creation) is handled by ngOnInit's toggle subscription;
    // reacting to it here would double-fetch.
    const changed = ["patChartId", "excludeAssessmentId"].some(
      (key) => changes[key] && !changes[key].isFirstChange()
    );
    if (changed) {
      if (this.stateData.concurrentVisitsToggle.getValue()) {
        this.loadOpenVisits();
      } else {
        this.lines = [];
        this.openVisits = [];
      }
    }
  }

  ngOnDestroy(): void {
    if (this.toggleSub) {
      this.toggleSub.unsubscribe();
    }
    if (this.permissionSub) {
      this.permissionSub.unsubscribe();
    }
  }

  // CO-3455 pop-back fix (CO-3382 wave-1 finding 6): ngOnInit and
  // ngOnChanges both fire at most once for a host page Ionic keeps cached on
  // the nav stack, so neither refires on a plain pop-back. Hosts that can be
  // popped back to call this from their own ionViewWillEnter.
  refresh(): void {
    if (this.stateData.concurrentVisitsToggle.getValue()) {
      this.loadOpenVisits();
    } else {
      this.lines = [];
      this.openVisits = [];
    }
  }

  // CO-3448 (BDD 4.12): index-correlated with `lines`, so the template can
  // activate a line without changing the `lines: string[]` shape every
  // existing content test (4.7/4.8/etc.) already asserts against verbatim.
  // Correlation is structural, not coincidental: loadOpenVisits assigns the
  // filtered array to this.openVisits FIRST and derives `lines` from that
  // same field (not a separate local), so the two can never disagree on
  // which visit a given index describes.
  activate(index: number): void {
    if (!this.canActivate) {
      return;
    }
    const visit = this.openVisits[index];
    if (!visit) {
      return;
    }
    this.beforeNavigate.emit();
    this.navSvc
      .resolvePatientSessionFromExternalId(this.externalPatChartId)
      .then(() =>
        this.navSvc.goToInternalPage("AssessmentHomePage", {
          patChartId: this.externalPatChartId,
          assessmentId: visit["_id"],
        })
      );
  }

  private loadOpenVisits(): void {
    if (!this.patChartId) {
      this.lines = [];
      this.openVisits = [];
      return;
    }
    this.dataService.getVisitsForOpenVisitIndicator(this.patChartId).subscribe(
      (response) => {
        const visits: any[] = (response && response["visits"]) || [];
        const openCodes: string[] = OPEN_VISIT_STATUS_SETS.OPEN_FOR_INDICATOR;
        this.openVisits = visits.filter(
          (v) =>
            openCodes.indexOf(v["asmt_status_lookup_code"]) !== -1 &&
            String(v["_id"]) !== String(this.excludeAssessmentId)
        );
        this.lines = this.openVisits.map((v) => this.buildLine(v));
      },
      () => {
        this.lines = [];
        this.openVisits = [];
      }
    );
  }

  private buildLine(visit: any): string {
    const clinician = visit["performed_by"] || "";
    const discipline = this.shortDiscipline(visit["role_lookup_value"]);
    // started_dte is null while a visit is In Progress; system_started_dte is
    // the creation instant (see startOfVisitMs in is-likely-duplicate-start.ts).
    const when = this.formatWhen(
      visit["started_dte"] || visit["system_started_dte"],
      visit["timezone"]
    );
    // Locked copy (00-contracts.md), byte-exact including the em dash -
    // assets/i18n/en.json "openVisitIndicatorLine".
    return this.translate.instant("openVisitIndicatorLine", {
      clinician,
      discipline,
      when,
    });
  }

  // VERIFY-TASK (track-C-modal-surfaces.md point 1): only Registered Nurse's
  // role_lookup_value ("Registered Nurse (RN)") embeds a parenthetical short
  // code in clinical-codes.constants.ts's CLINICAL_ROLE_SD002_CODES; Social
  // Worker/Hospice Aide/Medical Director/etc. do not. Extract the parenthetical
  // when present; otherwise fall back to the full discipline name rather than
  // inventing an abbreviation the codebase does not carry. See report.
  private shortDiscipline(roleLookupValue: string): string {
    const value = roleLookupValue || "";
    const match = value.match(/\(([^)]+)\)\s*$/);
    return match ? match[1] : value;
  }

  private formatWhen(startedDte: string, timezone: string): string {
    if (!startedDte) {
      return "";
    }
    const start = timezone
      ? moment.utc(startedDte).tz(timezone)
      : moment.utc(startedDte);
    const now = timezone ? moment().tz(timezone) : moment();
    const isToday = start.format("YYYY-MM-DD") === now.format("YYYY-MM-DD");
    // NOTE: 00-contracts.md's own token notation says "{h:mm AM/PM}"
    // (unpadded), but the mockup (State 2/3) and every BDD worked example
    // render a padded hour ("08:14 AM", never "8:14 AM"). Following the
    // concrete mockup/BDD evidence over the abstract token spelling - see
    // report deviations.
    return isToday
      ? `today ${start.format("hh:mm A")}`
      : start.format("YYYY-MM-DD HH:mm");
  }
}



// WEBPACK FOOTER //
// ./src/components/common/open-visit-indicator/open-visit-indicator.ts